import { Component, OnInit, ViewEncapsulation, Inject, ViewChild, OnDestroy } from '@angular/core';
import { RegionService } from '@app/shared/services/api/regions.service';
import { orderBy, toLower, trim } from 'lodash';
import { Observable, Subject, forkJoin, throwError } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RegionMunicipalitiesService } from '@app/shared/services/api/region-municipalities.service';
import { listToTree } from '@app/shared/utility';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { Node, Item, ListTreeComponent } from '@app/shared/components/list-tree/list-tree.component';
import { RegionManagementDataService } from '@app/region-management/services/region-management-data.service';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { RegionTypeEnum } from '@app/shared/enums';
import { Strings } from '@app/shared/constants';

@Component({
  selector: 'app-reassignment-dialog',
  templateUrl: './reassignment-dialog.component.html',
  styleUrls: ['./reassignment-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ReassignmentDialogComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  modalLoader: boolean = false;
  dataToMoveType: string = '';
  destinationType: string = '';
  destinationTypeName: string = '';
  listSource: any[] = [];
  treeSource: any[] = [];
  hasSelection: boolean = false;
  municipalitiesToMove: any[] = [];
  regionsToMove: any[] = [];
  @ViewChild('tree', ) tree: ListTreeComponent | undefined;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private matDialogRef: MatDialogRef<ReassignmentDialogComponent>,
    private regionsService: RegionService,
    private regionMunicipalitiesService: RegionMunicipalitiesService,
    private overlayLoader: OverlayLoaderService,
    private regionManagementData: RegionManagementDataService
  ) {}

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public ngOnInit(): void {
    this.modalLoader = true;
    this.municipalitiesToMove = this.data.selections.filter((a: any) => a.item.data.type.code === 'MUNICIPALITY');
    this.regionsToMove = this.data.selections.filter((a: any) => a.item.data.type.code !== 'MUNICIPALITY');

    if (this.regionsToMove.length > 0) {
      this.listSource = this.regionsToMove;
      this.dataToMoveType = this.listSource[0].item.data.type.name;
    } else {
      this.listSource = this.municipalitiesToMove;
      this.dataToMoveType = 'Kommunen';
    }

    this.destinationType = this.listSource[0].item.data.parent.type.guid;
    this.destinationTypeName = this.listSource[0].item.data.parent.type.name;
    const destinationCode = this.listSource[0].item.data.parent.type.code;

    this.regionManagementData.regions$.pipe(takeUntil(this.unsubscribe$)).subscribe((result: any[]) => {
      result = orderBy(result, ['name'], 'asc');
      result.forEach((a) => {
        a['selectable'] = a.regionTypeId === this.destinationType;
        return a;
      });

      let tree = listToTree(result, 'guid', 'parentRegionId', this.mapToTreeNode.bind(this))
        .filter((a) => a.item.data.regionTypeId === this.data.hierarchy.guid)
        .sort((a, b) => a.item.name.toLocaleLowerCase().localeCompare(b.item.name.toLocaleLowerCase()));

      if (destinationCode === RegionTypeEnum.adminAreaRegion) {
        const unassignedRegion = tree.find((a) => trim(toLower(a.item.name)) === trim(toLower(Strings.unassignedRegion)));
        if (unassignedRegion) {
          unassignedRegion.item.name = '';
        }
        tree = orderBy(tree, (a) => a.item.name, ['asc']);
      }

      this.treeSource = tree;
      this.modalLoader = false;
    });
  }

  mapToTreeNode(item: any, parent: any) {
    const treeNode = {} as Node;
    treeNode.item = {} as Item;
    treeNode.item.id = item.guid;
    treeNode.item.name = item.name;
    treeNode.item.selectable = item.selectable;
    treeNode.item.data = {
      regionTypeId: item.regionTypeId,
      parentRegion: parent ? { id: parent.guid, name: parent.name, type: parent.regionTypeId } : null,
    };
    if (item.regionTypeId !== this.destinationType) {
      treeNode.children = item.children.sort((a: any, b: any) => a.item.name.toLocaleLowerCase().localeCompare(b.item.name.toLocaleLowerCase()));
    }
    return treeNode;
  }

  trackByFn(_: number, item: any) {
    return item.municipalityId;
  }

  save() {
    const selectedDestination = this.tree?.selectedNodes[0];

    if (selectedDestination) {
      let update$: Observable<any>[] = [];

      if (this.data.hierarchy.code === 'AM') {
        update$ = this.getAdminRegionUpdates();
      }

      if (this.data.hierarchy.code === 'BOR') {
        update$ = this.getBackOfficeRegionUpdates();
      }

      if (this.data.hierarchy.code === 'SR') {
        update$ = this.getServiceRegionUpdates();
      }

      if (update$.length > 0) {
        this.overlayLoader.isLoading = true;
        forkJoin(update$)
          .pipe(
            tap(() => this.regionManagementData.loadData()),
            catchError((error: Error) => {
              this.overlayLoader.isLoading = false;
              this.matDialogRef.close(null);
              return throwError(() => error);
            }),
            takeUntil(this.unsubscribe$)
          )
          .subscribe(() => {
            this.overlayLoader.isLoading = false;
            this.matDialogRef.close(true);
          });
      }
    }
  }

  getAdminRegionUpdates() {
    const selectedDestination = this.tree?.selectedNodes[0];
    const update$: any[] = [];

    this.municipalitiesToMove.forEach((municipality) => {
      const adminRegionUpdate = {
        regionId: selectedDestination?.item.id,
        municipalityId: municipality.item.data.guid,
      };

      update$.push(this.regionMunicipalitiesService.putMunicipalityRegionMapping(municipality.item.data.regionMapping.adminRegionId, adminRegionUpdate));
    });

    return update$;
  }

  getBackOfficeRegionUpdates() {
    const selectedDestination = this.tree?.selectedNodes[0];
    const update$: any[] = [];

    if (this.regionsToMove.length > 0) {
      this.regionsToMove.forEach((region) => {
        const kamRegionParentUpdate = {
          parentRegionId: selectedDestination?.item.id,
        };
        update$.push(this.regionsService.put(region.item.id, kamRegionParentUpdate));
      });

      this.municipalitiesToMove.forEach((municipality) => {
        const backOfficeUpdate = {
          regionId: selectedDestination?.item.id,
          municipalityId: municipality.item.data.guid,
        };
        update$.push(this.regionMunicipalitiesService.putMunicipalityRegionMapping(municipality.item.data.regionMapping.backOfficeRegionId, backOfficeUpdate));
      });
    } else {
      this.municipalitiesToMove.forEach((municipality) => {
        const backOfficeUpdate = {
          regionId: selectedDestination?.item.data.parentRegion.id,
          municipalityId: municipality.item.data.guid,
        };

        const kamRegionUpdate = {
          regionId: selectedDestination?.item.id,
          municipalityId: municipality.item.data.guid,
        };

        update$.push(this.regionMunicipalitiesService.putMunicipalityRegionMapping(municipality.item.data.regionMapping.backOfficeRegionId, backOfficeUpdate));
        update$.push(this.regionMunicipalitiesService.putMunicipalityRegionMapping(municipality.item.data.regionMapping.kamRegionId, kamRegionUpdate));
      });
    }

    return update$;
  }

  getServiceRegionUpdates() {
    const selectedDestination = this.tree?.selectedNodes[0];
    const update$: any[] = [];

    if (this.regionsToMove.length > 0) {
      this.regionsToMove.forEach((region) => {
        const custServiceParentUpdate = {
          parentRegionId: selectedDestination?.item.id,
        };

        update$.push(this.regionsService.put(region.item.id, custServiceParentUpdate));
      });

      this.municipalitiesToMove.forEach((municipality) => {
        const serviceRegionUpdate = {
          regionId: selectedDestination?.item.id,
          municipalityId: municipality.item.data.guid,
        };

        update$.push(this.regionMunicipalitiesService.putMunicipalityRegionMapping(municipality.item.data.regionMapping.serviceRegionId, serviceRegionUpdate));
      });
    } else {
      this.municipalitiesToMove.forEach((municipality) => {
        const serviceRegionUpdate = {
          regionId: selectedDestination?.item.data.parentRegion.id,
          municipalityId: municipality.item.data.guid,
        };

        const custServiceRegionUpdate = {
          regionId: selectedDestination?.item.id,
          municipalityId: municipality.item.data.guid,
        };

        update$.push(this.regionMunicipalitiesService.putMunicipalityRegionMapping(municipality.item.data.regionMapping.serviceRegionId, serviceRegionUpdate));
        update$.push(
          this.regionMunicipalitiesService.putMunicipalityRegionMapping(municipality.item.data.regionMapping.customerServiceRegionId, custServiceRegionUpdate)
        );
      });
    }

    return update$;
  }

  cancel() {
    this.matDialogRef.close(false);
  }
}
